/**
 * Document Entity Mapping Service
 * 
 * Maps AI-extracted document data to entity form fields.
 * Bridges the gap between raw AI extraction output and the field formats
 * expected by entity creation forms (AddFlightForm, AddHotelForm, etc.)
 * 
 * @module documentEntityMappingService
 */

import {
    getAvailableEntityTypes,
    getSuggestedEntityType,
    getEntityType,
    isEntityTypeEnabled
} from '../config/entityTypeRegistry';

/**
 * Maps flight extraction data to AddFlightForm format
 * @param {Object} extractedData - Raw AI-extracted flight data
 * @returns {Object} Data formatted for AddFlightForm initialData prop
 */
const mapToFlightForm = (extractedData) => {
    if (!extractedData) return {};

    return {
        airline: extractedData.airline || '',
        flightNumber: extractedData.flightNumber || '',
        departureCity: extractedData.departureCity || '',
        departureAirport: extractedData.departureAirport || '',
        arrivalCity: extractedData.arrivalCity || '',
        arrivalAirport: extractedData.arrivalAirport || '',
        // Convert ISO strings to Date objects for DatePicker
        departureTime: extractedData.departureTime ? new Date(extractedData.departureTime) : null,
        arrivalTime: extractedData.arrivalTime ? new Date(extractedData.arrivalTime) : null,
        bookingReference: extractedData.bookingReference || '',
        ticketNumbers: extractedData.ticketNumbers || [],
        // Map passengers array to form's expected structure
        passengers: (extractedData.passengers || []).map((name, index) => ({
            firstName: name.split(' ')[0] || '',
            lastName: name.split(' ').slice(1).join(' ') || '',
            seat: extractedData.seatNumbers?.[index] || '',
            mealPreference: 'none'
        })),
        class: extractedData.classOfService || '',
        classOfService: extractedData.classOfService || '',
        baggage: extractedData.baggageAllowance || '',
        baggageAllowance: extractedData.baggageAllowance || '',
        notes: ''
    };
};

/**
 * Maps hotel extraction data to AddHotelForm format
 * @param {Object} extractedData - Raw AI-extracted hotel data
 * @returns {Object} Data formatted for AddHotelForm initialData prop
 */
const mapToHotelForm = (extractedData) => {
    if (!extractedData) return {};

    return {
        name: extractedData.hotelName || '',
        address: extractedData.address || '',
        city: extractedData.city || '',
        country: extractedData.country || '',
        // Convert ISO strings to Date objects
        checkInDate: extractedData.checkInDate ? new Date(extractedData.checkInDate) : null,
        checkOutDate: extractedData.checkOutDate ? new Date(extractedData.checkOutDate) : null,
        checkInTime: extractedData.checkInTime || '',
        checkOutTime: extractedData.checkOutTime || '',
        roomType: extractedData.roomType || '',
        numberOfRooms: extractedData.numberOfRooms || 1,
        numberOfGuests: extractedData.numberOfGuests || 1,
        guests: extractedData.guestNames || [],
        bookingReference: extractedData.bookingReference || '',
        confirmationNumber: extractedData.bookingReference || '',
        // Cost information
        cost: extractedData.cost || '',
        totalCost: extractedData.cost || '',
        costPerNight: extractedData.costPerNight || '',
        // Additional details
        amenities: extractedData.amenities || [],
        cancellationPolicy: extractedData.cancellationPolicy || '',
        phone: extractedData.contactPhone || '',
        email: extractedData.contactEmail || '',
        notes: ''
    };
};

/**
 * Maps car rental extraction data to AddCarForm format
 * @param {Object} extractedData - Raw AI-extracted car rental data
 * @returns {Object} Data formatted for AddCarForm initialData prop
 */
const mapToCarForm = (extractedData) => {
    if (!extractedData) return {};

    return {
        rentalCompany: extractedData.rentalCompany || '',
        bookingReference: extractedData.bookingReference || '',
        pickupLocation: extractedData.pickupLocation || '',
        returnLocation: extractedData.returnLocation || '',
        // Convert ISO strings to Date objects for DatePicker
        pickupTime: extractedData.pickupTime ? new Date(extractedData.pickupTime) : null,
        returnTime: extractedData.returnTime ? new Date(extractedData.returnTime) : null,
        vehicleClass: extractedData.vehicleClass || '',
        // Additional fields from extraction (may be used for notes or future form expansion)
        vehicleModel: extractedData.vehicleModel || '',
        driverName: extractedData.driverName || '',
        cost: extractedData.cost || '',
        costPerDay: extractedData.costPerDay || '',
        insuranceIncluded: extractedData.insuranceIncluded || false,
        mileagePolicy: extractedData.mileagePolicy || '',
        fuelPolicy: extractedData.fuelPolicy || '',
        // Build notes from additional extracted info not covered by form fields
        notes: [
            extractedData.vehicleModel ? `Vehicle: ${extractedData.vehicleModel}` : '',
            extractedData.driverName ? `Driver: ${extractedData.driverName}` : '',
            extractedData.mileagePolicy ? `Mileage: ${extractedData.mileagePolicy}` : '',
            extractedData.fuelPolicy ? `Fuel Policy: ${extractedData.fuelPolicy}` : '',
            extractedData.insuranceIncluded ? 'Insurance included' : ''
        ].filter(Boolean).join('\n')
    };
};

/**
 * Entity type to mapper function mapping
 */
const formMappers = {
    flight: mapToFlightForm,
    hotel: mapToHotelForm,
    car: mapToCarForm
};

/**
 * Document Entity Mapping Service
 * Provides functions to map extracted document data to entity forms
 */
export const documentEntityMappingService = {
    /**
     * Get available entity types for a document's category
     * Returns hierarchical list organized by category
     * 
     * @param {string} primaryCategory - Document's primaryCategory field
     * @returns {Array} Array of category objects with their entity types
     */
    getAvailableEntityTypes(primaryCategory) {
        return getAvailableEntityTypes(primaryCategory);
    },

    /**
     * Get the suggested entity type for a document category
     * Returns the default entity type that should be pre-selected
     * 
     * @param {string} primaryCategory - Document's primaryCategory field
     * @returns {Object|null} Suggested entity type or null
     */
    getSuggestedEntityType(primaryCategory) {
        return getSuggestedEntityType(primaryCategory);
    },

    /**
     * Get entity type configuration by ID
     * 
     * @param {string} entityTypeId - Entity type ID (e.g., 'flight', 'hotel')
     * @returns {Object|null} Entity type configuration
     */
    getEntityType(entityTypeId) {
        return getEntityType(entityTypeId);
    },

    /**
     * Check if an entity type is enabled for use
     * 
     * @param {string} entityTypeId - Entity type ID
     * @returns {boolean} Whether the entity type is enabled
     */
    isEntityTypeEnabled(entityTypeId) {
        return isEntityTypeEnabled(entityTypeId);
    },

    /**
     * Map extracted AI data to form field format for a specific entity type
     * 
     * @param {Object} extractedData - Raw data from AI extraction
     * @param {string} entityType - Target entity type ('flight', 'hotel', etc.)
     * @returns {Object} Data formatted for the entity's form component
     */
    mapToFormData(extractedData, entityType) {
        const mapper = formMappers[entityType];
        if (!mapper) {
            console.warn(`No form mapper defined for entity type: ${entityType}`);
            return extractedData || {};
        }
        return mapper(extractedData);
    },

    /**
     * Get the form component name for an entity type
     * 
     * @param {string} entityType - Entity type ID
     * @returns {string|null} Form component name ('AddTravel', 'AddAccommodation', etc.)
     */
    getFormComponent(entityType) {
        const entityConfig = getEntityType(entityType);
        return entityConfig?.formComponent || null;
    },

    /**
     * Get the form props for an entity type
     * These are passed to the form component to configure it for the specific subtype
     * 
     * @param {string} entityType - Entity type ID
     * @returns {Object} Props to pass to the form component
     */
    getFormProps(entityType) {
        const entityConfig = getEntityType(entityType);
        return entityConfig?.formProps || {};
    },

    /**
     * Get category for a document category
     * Maps document categories to entity categories
     * 
     * @param {string} documentCategory - Document's primaryCategory
     * @returns {string|null} Corresponding entity category
     */
    getEntityCategory(documentCategory) {
        // Document categories map to entity categories
        const categoryMap = {
            'travel': 'travel',
            'accommodation': 'accommodation',
            'location': 'place'
        };
        return categoryMap[documentCategory] || null;
    }
};

export default documentEntityMappingService;
